<template>
  <div class="modern-form message-logs-wrapper">
    <v-container>
      <div class="text-center" v-if="loading">
        <v-progress-circular
          indeterminate
          color="primary"
          class="loading-spinner" />
      </div>

      <div v-else class="messages-table">
        <div v-if="messages.length === 0" class="no-results">
          <v-icon class="no-results-icon">mdi-message-off</v-icon>
          <p class="no-results-text">No messages found</p>
        </div>
        <template v-else>
        <div class="table-header">
          <div class="header-cell patient-col">Patient</div>
          <div class="header-cell direction-col">Sent/Received</div>
          <div class="header-cell message-col">Message</div>
          <div class="header-cell date-col">Date</div>
        </div>
        <div class="table-body">
          <div
            v-for="item in messages"
            :key="item.id"
            @click="openDetails(item)"
            class="message-row">
            <div class="message-cell patient-col">
              <span v-if="!item.outgoing && item.unread" class="unread-dot"></span>
              <span :class="{ 'unread-text': !item.outgoing && item.unread }">
                {{ item.patientFirstName }} {{ item.patientLastName }}
              </span>
            </div>
            <div class="message-cell direction-col">
              <v-icon small class="direction-icon">
                {{ item.outgoing ? 'mdi-send' : 'mdi-message-arrow-left' }}
              </v-icon>
              {{ item.outgoing ? 'Sent' : 'Received' }}
            </div>
            <div
              class="message-cell message-col"
              :class="{ 'unread-text': !item.outgoing && item.unread }">
              {{ item.message }}
            </div>
            <div class="message-cell date-col">
              {{ Utils.formatDate(item.date) }} {{ Utils.formatTime(item.date) }}
            </div>
          </div>
        </div>
        </template>
      </div>
      
      <div v-if="!loading" class="pagination-container">
        <div class="pagination-size">
          <v-select
            v-model="pageSize"
            :items="pageSizes"
            label="Items"
            dense
            outlined
            hide-details
            @change="handlePageSizeChange" />
        </div>
        <v-pagination
          v-model="page"
          :length="totalPages"
          total-visible="5"
          next-icon="mdi-menu-right"
          prev-icon="mdi-menu-left"
          class="custom-pagination"
          @input="handlePageChange" />
      </div>

      <v-dialog v-model="showMessageDialog" max-width="600px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Message Details</v-card-title>
          <v-card-text class="modal-content">
            <v-row>
              <v-col cols="6">
                <h4 class="detail-label">Patient</h4>
                <span class="detail-value patient-link" @click="goToPatient(selectedMessage.patientId)">
                  {{ selectedMessage.patientFirstName }} {{ selectedMessage.patientLastName }}
                </span>
                <br>
                <span class="detail-value">{{ selectedMessage.patientPhoneNumber }}</span>
              </v-col>
              <v-col cols="6">
                <h4 class="detail-label">Date</h4>
                <span class="detail-value">{{ Utils.formatDate(selectedMessage.date) }} {{ Utils.formatTime(selectedMessage.date) }}</span>
              </v-col>
            </v-row>
            <v-row class="mb-2">
              <v-col>
                <h4 class="detail-label">Message</h4>
                <p class="detail-value">{{ selectedMessage.message }}</p>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showMessageDialog = false">Close</v-btn>
            <v-btn class="btn-outline-primary" @click="goToPatient(selectedMessage.patientId)">View Patient</v-btn>
            <v-btn
              v-if="!selectedMessage.outgoing"
              class="btn-primary"
              @click="toggleReadStatus()">
              Mark {{ selectedMessage.unread ? 'Read' : 'Unread' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import DataService from '../../../_services/DataService';
import Utils from '../../../src/utils.js';

export default {
  name: 'SMS',
  data() {
    return {
      pageName: 'SMS',
      pageIcon: 'mdi-message-text',
      isLoaded: false,
      loading: true,
      showMessageDialog: false,
      selectedMessage: {},
      messages: [],
      page: 1,
      totalPages: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100, 200],
      headers: [{ text: 'Patient' }, { text: 'Sent/Received' }, { text: 'Message' }, { text: 'Date' }],
      Utils: Utils,
    };
  },
  computed: {
    activeCols: function () {
      return this.headers.filter(function (u) {
        return u.active == 1;
      });
    },
  },
  methods: {
    async openDetails(message) {
      this.selectedMessage = message;

      if (message.unread) {
        await this.toggleReadStatus(message);
      }

      this.showMessageDialog = true;
    },
    async fetchMessages() {
      this.loading = true;
      const params = {
        size: this.pageSize,
        page: this.page,
      };

      const response = await DataService.fetchMessages(params);
      if (response.status === 200) {
        this.messages = response.data.messages;
        this.totalPages = response.data.pageCount;
      } else {
        this.$root.$emit('raise_alert', Utils.setAlert('fail'));
      }
      this.loading = false;
    },
    async handlePageChange(value) {
      this.page = value;
      await this.fetchMessages();
    },
    handleRowClick(value) {
      if (this.clickable == true) {
        this.$parent.handleRowClick(value);
      }
    },
    goToPatient(patientId) {
      this.$router.push({
        name: 'Admin',
        query: { tab: 'patient management', patientId },
      });
    },
    async handlePageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      await this.fetchMessages();
    },
    async toggleReadStatus() {
      let response;

      if (this.selectedMessage.unread) {
        response = await DataService.markSmsAsRead(this.selectedMessage.id);
      } else {
        response = await DataService.markSmsAsUnread(this.selectedMessage.id);
      }

      if (response.status !== 200) {
        this.$root.$emit('raise_alert', Utils.setAlert('fail'));
      } else {
        this.selectedMessage.unread = !this.selectedMessage.unread;
      }

      await this.fetchMessages();
    },
  },
  async mounted() {
    await this.fetchMessages();
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

$table-header-bg-color: #f8fafc;
$table-header-text-color: #374151;
$table-border-color: #e2e8f0;
$message-row-bg-color: white;
$message-row-hover-bg: #f8fafc;
$message-row-border-color: #f1f5f9;
$message-cell-text-color: #374151;
$direction-icon-color: #6b7280;
$unread-dot-color: #2c5aa0;
$detail-label-color: #374151;
$detail-value-color: #6b7280;
$patient-link-color: #2c5aa0;
$patient-link-hover-color: #1a365d;
$pagination-border-color: #e2e8f0;
$pagination-active-bg: #133b54;
$no-results-icon-color: #cbd5e0;
$no-results-text-color: #718096;

.messages-table {
  .table-header {
    display: grid;
    grid-template-columns: 2fr 1.5fr 3fr 2fr;
    background: $table-header-bg-color;
    border-bottom: 1px solid $table-border-color;
    
    @media (max-width: $breakpoint-mobile) {
      display: none;
    }
    
    .header-cell {
      padding: 16px 24px;
      font-size: 14px;
      font-weight: 600;
      color: $table-header-text-color;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      
      @media (max-width: $breakpoint-mobile) {
        padding: 12px 16px;
        font-size: 12px;
      }
    }
  }
  
  .table-body {
    .message-row {
      display: grid;
      grid-template-columns: 2fr 1.5fr 3fr 2fr;
      border-bottom: 1px solid $message-row-border-color;
      cursor: pointer;
      transition: background-color $transition-fast ease;
      
      @media (max-width: $breakpoint-mobile) {
        grid-template-columns: 1fr;
        gap: 8px;
        padding: 16px;
      }
      
      &:hover {
        background-color: $message-row-hover-bg;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      .message-cell {
        padding: 16px 24px;
        font-size: 14px;
        color: $message-cell-text-color;
        display: flex;
        align-items: center;
        
        @media (max-width: $breakpoint-mobile) {
          padding: 8px 0;
          font-size: 15px;
          line-height: 1.4;
          flex-wrap: wrap;
          
          &::before {
            font-weight: 600;
            color: $table-header-text-color;
            margin-right: 8px;
            flex-shrink: 0;
          }
        }
        
        &.patient-col {
          position: relative;
          gap: 8px;
          @media (max-width: $breakpoint-mobile) {
            &::before {
              content: 'Patient:';
            }
          }
        }
        
        &.direction-col {
          gap: 8px;
          
          .direction-icon {
            color: $direction-icon-color;
          }
          
          @media (max-width: $breakpoint-mobile) {
            &::before {
              content: 'Status:';
            }
          }
        }
        
        &.message-col {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          
          @media (max-width: $breakpoint-mobile) {
            white-space: normal;
            word-break: break-word;
            overflow-wrap: break-word;
            
            &::before {
              content: 'Message:';
            }
          }
        }
        
        &.date-col {
          @media (max-width: $breakpoint-mobile) {
            font-size: 14px;
            
            &::before {
              content: 'Date:';
            }
          }
        }
      }
    }
  }
}

  .pagination-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;
    padding: 12px 0;

    .pagination-size {
      width: 90px;
    }

    .custom-pagination {
      ::v-deep .v-pagination {
        &__item {
          box-shadow: none;
          border: 1px solid $pagination-border-color;

          &--active {
            background-color: $pagination-active-bg !important;
            color: white !important;
            border-color: $pagination-active-bg !important;
          }
        }

        &__more {
          width: 22px;
          margin-left: 0;
          margin-right: 0;
        }

        &__navigation {
          box-shadow: none;
          border: 1px solid $pagination-border-color;
        }
      }
    }
  }

.detail-label {
  font-size: 14px;
  font-weight: 600;
  color: $detail-label-color;
  margin-bottom: 8px;
}

.detail-value {
  font-size: 14px;
  color: $detail-value-color;
}

.patient-link {
  color: $patient-link-color;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: $patient-link-hover-color;
  }
}

.unread-dot {
  width: 8px;
  height: 8px;
  background-color: $unread-dot-color;
  border-radius: 50%;
  flex-shrink: 0;
  
  @media (max-width: $breakpoint-mobile) {
    width: 10px;
    height: 10px;
  }
}

.unread-text {
  font-weight: 600;
}

.loading-spinner {
  margin: 40px auto;
}

.no-results {
  text-align: center;
  padding: 80px 24px;
  
  .no-results-icon {
    font-size: 64px;
    color: $no-results-icon-color;
    margin-bottom: 16px;
  }
  
  .no-results-text {
    color: $no-results-text-color;
    margin: 0;
    font-size: 16px;
  }
}

// Wrapper styling to prevent pagination overflow
// When there are many pages, the pagination can extend beyond the container width
.message-logs-wrapper {
  .v-container {
    padding-bottom: 32px;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  .pagination-container {
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>
